import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import PageSEO from "@/components/PageSEO";
import { locationAPI } from "@/lib/api";
import { MapPin, Home, Loader2, ChevronRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultImage = "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=500&fit=crop";

const getImageUrl = (loc) => {
  if (!loc?.image) return defaultImage;
  if (loc.image.startsWith("http")) return loc.image;
  return `${API_URL}${loc.image}`;
};

const LocationsPage = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await locationAPI.getAll();
        setLocations(res.data || []);
      } catch (err) {
        console.error("Failed to fetch locations:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  // Sort all locations by homestay count (highest first)
  const sortedLocations = [...locations].sort((a, b) => (b.homestay_count || 0) - (a.homestay_count || 0));

  return (
    <>
      <PageSEO
        title="Explore Locations in Konkan"
        description="Browse all homestay destinations across the Konkan coast — from Alibaug to Ganpatipule. Find your perfect getaway."
        path="/locations"
      />
      <Navbar />

      <main className="min-h-screen bg-[#FAF8F3]" data-testid="locations-page">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#2F5D3A]" />
          </div>
        ) : locations.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p className="text-lg">No locations available yet</p>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1.5 h-8 bg-[#2F5D3A] rounded-full" />
              <h2 className="text-2xl font-bold text-gray-900">All Destinations</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="all-locations-grid">
              {sortedLocations.map((loc) => (
                <LocationCard key={loc.id} location={loc} />
              ))}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </>
  );
};

const LocationCard = ({ location }) => {
  const [imgError, setImgError] = useState(false);
  const imgSrc = imgError ? defaultImage : getImageUrl(location);

  return (
    <Link
      to={`/location/${location.slug}`}
      className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
      data-testid={`location-card-${location.slug}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={imgSrc}
          alt={location.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={() => setImgError(true)}
          loading="lazy"
        />
        <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1.5 text-sm font-medium text-gray-700">
          <Home className="w-3.5 h-3.5 text-[#2F5D3A]" />
          {Math.max(location.homestay_count || 0, 0)} Homestay{Math.max(location.homestay_count || 0, 0) !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#2F5D3A] transition-colors truncate">
              {location.name}
            </h3>
            {location.state && (
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                {location.state}
              </p>
            )}
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#2F5D3A] group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" />
        </div>

        {location.description && (() => {
          const text = location.description.replace(/<[^>]*>/g, "").trim();
          return text ? (
            <p className="text-sm text-gray-600 mt-3 line-clamp-2 leading-relaxed">
              {text}
            </p>
          ) : null;
        })()}
      </div>
    </Link>
  );
};

export default LocationsPage;
